// "Client feedback" — Practice Details card, staff only (the backend refuses the client
// role). The last ten star ratings the practice's weekly report recipients sent back,
// with their comments, newest first. Read-only: the page is the email's link.
// `embedded`: a subsection inside the Weekly report card (admins) rather than a card
// of its own (staff who cannot see that card).

import React, { useEffect, useState } from "react";
import "./ClientFeedback.css";

const API_URL = process.env.REACT_APP_API_URL || "";

const url = (practiceId) => `${API_URL}/api/practices/${practiceId}/client-feedback`;

function stars(n) {
  const count = Math.max(0, Math.min(5, Number(n) || 0));
  return "★".repeat(count) + "☆".repeat(5 - count);
}

function when(row) {
  const iso = row.updated_at || row.submitted_at;
  if (!iso) return "";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleDateString(undefined, { year: "numeric", month: "short", day: "numeric" });
}

export default function ClientFeedbackCard({ practiceId, embedded = false }) {
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!practiceId) return undefined;
    let cancelled = false;
    setData(null);
    setError(null);
    fetch(url(practiceId), { credentials: "include" })
      .then(async (resp) => {
        const body = await resp.json().catch(() => null);
        if (!resp.ok) throw new Error((body && body.error) || "Could not load the client feedback");
        if (!body || !Array.isArray(body.rows)) throw new Error("The feedback could not be read.");
        return body;
      })
      .then((body) => {
        if (!cancelled) setData(body);
      })
      .catch((e) => {
        if (!cancelled) setError(e.message);
      });
    return () => {
      cancelled = true;
    };
  }, [practiceId]);

  const rows = (data && data.rows) || [];
  const pending = data && data.available === false;

  return (
    <div
      className={embedded ? "client-feedback-embedded" : "integrations-section client-feedback-card"}
      data-testid="client-feedback-card"
    >
      <div className="weekly-reports-header">
        {embedded ? <h4 className="client-feedback-title">Client feedback</h4> : <h3>Client feedback</h3>}
        {data && !pending && (
          <span className={`weekly-reports-badge ${rows.length ? "on" : "off"}`}>
            {rows.length ? `${rows.length} recent` : "None yet"}
          </span>
        )}
      </div>
      {pending ? (
        <p className="weekly-reports-pending">Unavailable until migration {data.migration} has run.</p>
      ) : null}
      {error && (
        <div className="weekly-reports-error" role="alert">
          {error}
          <button type="button" className="weekly-reports-dismiss" onClick={() => setError(null)} aria-label="Dismiss">×</button>
        </div>
      )}
      {data && !pending && rows.length === 0 && (
        <p className="client-feedback-empty">No feedback yet</p>
      )}
      {rows.length > 0 && (
        <table className="client-feedback-table">
          <thead>
            <tr>
              <th>Rating</th>
              <th>Comment</th>
              <th>From</th>
              <th>When</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((row) => (
              <tr key={row.id}>
                <td className="client-feedback-stars" title={`${row.stars} of 5`}>{stars(row.stars)}</td>
                <td className="client-feedback-comment">{row.comment || <span className="client-feedback-muted">—</span>}</td>
                <td className="client-feedback-from">{row.recipient_email}</td>
                <td className="client-feedback-when">{when(row)}{row.updated_at ? " (updated)" : ""}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}
